import { ModalEditComponentProps } from '../../../../../../baseComponents/ModalsLayouts/types'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { FormRender } from '../../../../../../forms/FormRenderer/types'
import { valueOrDefaultValue } from '../../../../../../utils/globals/valueOrDefaultValue'
import { LittleModalCreateEdit } from '../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { permissionCheck } from '../../../../../../baseComponents/PermissionChecker/function'
import { SellVariableModelType, SellVariableTypeInversed, SellVariableTypeKeys } from '@teinor/erp/types/company/config/sellVariable'
import { BeforeSubmitHandler, OnSubmit } from '../../../../../../forms/TwinForm/types'
import { useCallback, useState } from 'react'
import { changeErrorMessage } from '../../../../../../utils/reducers/reduxDispatch'
import { onlyWordsChecker } from '../../../../../../utils/globals/checkers'
import { RowData } from '../../../../../../baseComponents/TwinTable/types'
import { ButtonPrimary } from '../../../../../../baseComponents/Button'
import TwinTrans from '../../../../../../baseComponents/TwinTrans'
import { InputWithLabelMargin } from '../../../../../../forms/Input'
import { TwinIconToolTip } from '../../../../../../baseComponents/TwinIcon'
import { faXmark } from '@fortawesome/pro-light-svg-icons'
import { getOptionsFromExtraDataSellVariable, parseOptionsSellVariable } from './functions'

interface ModalCESellVariableProps extends ModalEditComponentProps { }

const ModalCESellVariable: React.FC<ModalCESellVariableProps> = ({ allRowData, userPermissions, ...rest }) => {
    const { t } = useTwinTranslation()
    const { handleOnError, idCheckerKeyDown, myType, setMyType, options, setOptions, beforeSubmitHandler } = useModalCESellVariableLogic({ allRowData })
    const dictTypes:  Record<SellVariableTypeKeys, {name: string, value: number}> = {
        0: { name: t('number', 'Número'), value: 0 },
        1: { name: t('text', 'Texto') , value: 1 },
        2: { name: t('price', 'Precio'), value: 2 },
        3: { name: t('selector', 'Selector'), value: 3 }
    }
    const fields: FormRender<SellVariableModelType> = [
        {
            cols: 1,
            elements: [
                {
                    name: 'id',
                    label: t('id', 'ID'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true,
                    disabled: allRowData?.id,
                    onKeyDown: idCheckerKeyDown
                },
                {
                    name: 'name',
                    label: t('name', 'Nombre'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true,
                },
                {
                    name: 'type',
                    label: t('variableType', 'Tipo de variable'),
                    component: 'CustomSelectWithSearchBar',
                    items: dictTypes,
                    required: true,
                    value: myType,
                    readOnly: allRowData?.id,
                    onChange: setMyType,
                },
            ]
        },
    ]
    let mySelectorRender: JSX.Element | null = null
    if (parseInt(String(myType)) === SellVariableTypeInversed['selector']){
        mySelectorRender = <SelectorOptions {...{ options, setOptions }} />
    }

    if (allRowData && userPermissions) {
        const parsedFields = valueOrDefaultValue(fields, userPermissions, allRowData)
        return (<LittleModalCreateEdit fields={parsedFields} url={'/api/app/config/sell/sellVariable/updateSellVariable'} translations={{
             title: t('editSellVariable', 'Editar variable de venta'), 
             button: t('save', 'Guardar') 
            }} haveButtonPermissions={permissionCheck(userPermissions, 'update')} beforeSubmit={beforeSubmitHandler} {...rest} className='notFlexAutoFormRender'>
                {mySelectorRender}
            </LittleModalCreateEdit>)
    }
    return (<LittleModalCreateEdit fields={fields} url={'/api/app/config/sell/sellVariable/createSellVariable'} translations={{
        title: t('createSellVariable', 'Crear variable de venta'),
        button: t('create', 'Crear')
    }} haveButtonPermissions={true} beforeSubmit={beforeSubmitHandler} {...rest} onChangeError={handleOnError} className='notFlexAutoFormRender'>
        {mySelectorRender}
    </LittleModalCreateEdit>)
}

interface ModalCESellVariableLogicProps {
    allRowData?: RowData
}

const useModalCESellVariableLogic = ({ allRowData }: ModalCESellVariableLogicProps) => {
    const [myType, setMyType] = useState<number>(allRowData?.type || 0)
    const [options, setOptions] = useState<string[]>(getOptionsFromExtraDataSellVariable(allRowData?.extraData || ''))

    const { t } = useTwinTranslation()
    const errorMessage = t('idUniqueAndAlreadyExists', 'El ID tiene que ser único y ya existe')

    const handleOnError: OnSubmit = useCallback(async (res: Response) => {
        const responseJSON = await res.json()
        if (responseJSON.errors[0].msg === 'This reference already exists') {
            changeErrorMessage(errorMessage)
        }
    }, [errorMessage])

    const idCheckerKeyDown: React.KeyboardEventHandler<HTMLInputElement> = useCallback((e) => {
         if (!onlyWordsChecker(e.key)){ 
            e.preventDefault()
        }
    }, [])

    const beforeSubmitHandler: BeforeSubmitHandler = useCallback((values) => {
        if (options.length > 0) {
            values.extraData = JSON.stringify(parseOptionsSellVariable(options))
        }
        return true
    }, [options])

    return { handleOnError, idCheckerKeyDown, myType, options, setOptions, beforeSubmitHandler, setMyType }
}

interface SelectorOptionsProps extends SelectorOptionsLogicProps {
    options: string[]
}

const SelectorOptions: React.FC<SelectorOptionsProps> = ({ options, ...logic }) => {
    const { handleAddOption, handleDeleteOption, handleChangeOption } = useSelectorOptionsLogic({ ...logic })
    const renderThis: JSX.Element[] = []
    if (options.length) {
        for (const key in options) {
            const value = options[key]
            const pos = parseInt(key)
            renderThis.push(<Option key={key} {...{ value, pos, handleDeleteOption, handleChangeOption }} />)
        }
    }
    return (
        <div>
            <div className='regular18 mb-10'>
                <TwinTrans transKey='selectorOptions'>Opciones selector</TwinTrans>
            </div>
            <div className='flex flex-col'>
                {renderThis}
            </div>
            <ButtonPrimary onClick={handleAddOption} type='button' className='mx-auto mt-20'>
                <TwinTrans transKey='addOption'>Añadir opción</TwinTrans>
            </ButtonPrimary>
        </div>
    )
}

interface SelectorOptionsLogicProps {
    setOptions: React.Dispatch<React.SetStateAction<string[]>>
}

const useSelectorOptionsLogic = ({ setOptions }: SelectorOptionsLogicProps) => {
    const handleAddOption = useCallback(() => {
        setOptions((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld.push('')
            return copyOld
        })
    }, [setOptions])

    const handleDeleteOption = useCallback((pos: number) => {
        setOptions((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld.splice(pos, 1)
            return copyOld
        })
    }, [setOptions])

    const handleChangeOption = useCallback((val: string, pos: number) => {
        setOptions((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld.splice(pos, 1, val)
            return copyOld
        })
    }, [setOptions])

    return { handleAddOption, handleDeleteOption, handleChangeOption }
}

interface OptionProps {
    pos: number
    value: string
    handleChangeOption: (val: string, pos: number)=> void
    handleDeleteOption: (pos: number) =>void
}

const Option: React.FC<OptionProps> = ({ pos, value, handleChangeOption, handleDeleteOption }) => {
    const {t} = useTwinTranslation()
    return (
        <div className='flex items-center my-10'>
            <InputWithLabelMargin className='w-full' value={value} onChange={(val) => handleChangeOption(val, pos)} label={t('option', 'Opción') + ' ' + (pos+1)} />
            <TwinIconToolTip classNameIcon='hover:text-red-BA' icon={faXmark} message={t('deleteOption', 'Eliminar opción')} id={'option' + pos} onClick={() => handleDeleteOption(pos)} />
        </div>
    )
}


export default ModalCESellVariable